// Global store — catalog + cart (client-only ตาม 05-FRONTEND: in-memory) + drawer + toast
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PRODUCTS } from '../data/catalog.js';
import { getCatalog } from './api.js';
import { useI18n } from './i18n.jsx';

const Ctx = createContext(null);
export const useStore = () => useContext(Ctx);

export function computeUnit(p, opt) {
  if (p.price === null || p.price === undefined) return null;
  let u = p.price;
  (p.options || []).forEach((o, oi) => { u += o.addPrice ? o.addPrice[opt ? opt[oi] : 0] : 0; });
  return u;
}

export function StoreProvider({ children }) {
  const { t } = useI18n();
  const [products, setProducts] = useState(PRODUCTS);
  const [catalogSource, setCatalogSource] = useState('seed');
  const [cart, setCart] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [toast, setToastMsg] = useState('');
  const toastTimer = useRef(null);
  const [lastOrder, setLastOrder] = useState(null); // ให้หน้า Confirm ใช้

  useEffect(() => {
    let on = true;
    getCatalog().then(({ products: ps, source }) => {
      if (on) { setProducts(ps); setCatalogSource(source); }
    });
    return () => { on = false; };
  }, []);

  const showToast = (m) => {
    setToastMsg(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 1700);
  };

  const P = (idOrSku) => products.find((p) => p.id === idOrSku || p.sku === idOrSku);

  const addToCart = (idOrSku, color, opt, qty = 1) => {
    const p = P(idOrSku);
    if (!p) return false;
    if (p.tag === 'sold') { showToast(t('toast.sold')); return false; }
    const unit = computeUnit(p, opt);
    if (unit === null) { showToast(t('toast.ask')); return false; }
    const vl = [];
    if (color) vl.push(color);
    (p.options || []).forEach((o, oi) => { if (opt && opt[oi] != null) vl.push(o.vals[opt[oi]]); });
    const key = p.id + '|' + vl.join('/');
    setCart((c) => {
      const ex = c.find((i) => i.key === key);
      if (ex) return c.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [...c, { key, id: p.id, sku: p.sku, cat: p.cat, kw: p.kw, imgs: p.imgs, name: p.name, unitPrice: unit, weight: p.weight, qty, variant: vl.join(' · '), optIdx: opt ? [...opt] : null, color: color || null }];
    });
    showToast(t('toast.added'));
    return true;
  };

  const setQty = (key, d) =>
    setCart((c) => c
      .map((i) => (i.key === key ? { ...i, qty: i.qty + d } : i))
      .filter((i) => i.qty > 0));

  const removeItem = (key) => setCart((c) => c.filter((i) => i.key !== key));
  const clearCart = () => setCart([]);

  const value = useMemo(() => ({
    products, catalogSource, P,
    cart, addToCart, setQty, removeItem, clearCart,
    cartQty: cart.reduce((s, i) => s + i.qty, 0),
    cartSubtotal: cart.reduce((s, i) => s + i.unitPrice * i.qty, 0),
    cartWeight: cart.reduce((s, i) => s + i.weight * i.qty, 0),
    drawerOpen, setDrawerOpen,
    searchOpen, setSearchOpen,
    toast, showToast,
    lastOrder, setLastOrder,
  }), [products, catalogSource, cart, drawerOpen, searchOpen, toast, lastOrder, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
